import { useState } from 'react';
import type { JSX } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { ConfirmDialog, ErrorState, Field, Panel, Toggle } from '../components/ui.js';
import '../screens-2c.css';

/**
 * Měření webu v Nastavení → Pravidla: kdy je návštěva „aktivní“, jak dlouho se nákup přičítá
 * zprávě, které akce jsou klíčové a jestli se aktivita z webu počítá do pole aktivity kontaktu.
 */

interface Rule {
  key: string;
  title: string;
  hint: string;
  unit: string;
  min: number;
  max: number;
}

const RULES: Rule[] = [
  {
    key: 'web_active_min_engaged_seconds',
    title: 'Aktivní návštěva od',
    hint: 'Kolik vteřin skutečné aktivity na webu stačí, aby se návštěva počítala jako aktivní. Klíčová akce stačí vždy. Platí jen pro nové návštěvy.',
    unit: 'vteřiny',
    min: 1,
    max: 600,
  },
  {
    key: 'web_attribution_window_hours',
    title: 'Nákup po návštěvě ze zprávy',
    hint: 'Do kolika hodin po návštěvě ze zprávy se zaplacená objednávka přičte této zprávě.',
    unit: 'hodiny',
    min: 1,
    max: 720,
  },
  {
    key: 'web_hub_msg_valid_days',
    title: 'Platnost označení odkazu',
    hint: 'Jak dlouho po odeslání zprávy se návštěva z jejího odkazu ještě přiřadí ke zprávě.',
    unit: 'dny',
    min: 1,
    max: 365,
  },
  {
    key: 'web_silent_after_hours',
    title: 'Web „mlčí“ po',
    hint: 'Kolik hodin bez jediné události z webu znamená, že web nejspíš přestal měřit.',
    unit: 'hodiny',
    min: 1,
    max: 720,
  },
];

export function WebSettings({
  canWrite,
  values,
  envName,
}: {
  canWrite: boolean;
  values: Record<string, unknown>;
  envName: string;
}): JSX.Element {
  const client = useQueryClient();
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [events, setEvents] = useState<string | null>(null);
  const [askActivity, setAskActivity] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);

  const patch = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.patch('/api/settings', body),
    onSuccess: () => {
      setError(null);
      setDraft({});
      setEvents(null);
      setAskActivity(null);
      void client.invalidateQueries({ queryKey: ['settings'] });
    },
    onError: (err: Error) => {
      setAskActivity(null);
      setError(err.message);
    },
  });

  const problem = (r: Rule): string | null => {
    const raw = draft[r.key];
    if (raw === undefined) return null;
    const n = Number(raw);
    if (raw.trim() === '' || !Number.isInteger(n) || n < r.min || n > r.max) {
      return `Zadejte celé číslo od ${r.min} do ${r.max}.`;
    }
    return null;
  };
  const changed = RULES.filter((r) => draft[r.key] !== undefined);
  const invalid = changed.some((r) => problem(r) !== null);

  const shownEvents =
    events ?? (Array.isArray(values.web_key_events) ? values.web_key_events.join(', ') : '');
  const parsedEvents = shownEvents
    .split(/[,\s]+/)
    .map((e) => e.trim())
    .filter(Boolean);
  const eventsInvalid = parsedEvents.some((e) => !/^[a-z][a-z0-9_]{0,39}$/.test(e));
  const activityOn = values.web_engagement_in_activity === true;

  return (
    <>
      {error ? (
        <ErrorState error={new Error(error)} title="Nastavení se nepodařilo uložit" />
      ) : null}
      <Panel
        title="Měření webu"
        subtitle="Jak se z návštěv webu po souhlasu s měřením počítá aktivita a nákup."
      >
        <div className="s2c-form">
          {RULES.map((r) => (
            <Field key={r.key} label={`${r.title} (${r.unit})`} hint={r.hint} error={problem(r)}>
              <input
                type="number"
                min={r.min}
                max={r.max}
                step={1}
                inputMode="numeric"
                className="s2c-num"
                value={draft[r.key] ?? String(values[r.key] ?? '')}
                disabled={!canWrite}
                aria-invalid={problem(r) !== null}
                onChange={(e) => setDraft({ ...draft, [r.key]: e.target.value })}
              />
            </Field>
          ))}
          <Field
            label="Klíčové akce na webu"
            hint="Názvy akcí oddělené čárkou, malými písmeny (například purchase, begin_checkout). Web je posílá voláním hub.key; jiné názvy hub ignoruje."
            error={eventsInvalid ? 'Jen malá písmena, číslice a podtržítko, začít písmenem.' : null}
          >
            <input
              value={shownEvents}
              disabled={!canWrite}
              aria-invalid={eventsInvalid}
              onChange={(e) => setEvents(e.target.value)}
            />
          </Field>
        </div>
        {canWrite ? (
          <div className="s2c-actions">
            <button
              type="button"
              className="btn pri"
              disabled={
                (changed.length === 0 && events === null) ||
                invalid ||
                eventsInvalid ||
                patch.isPending
              }
              onClick={() =>
                patch.mutate({
                  ...Object.fromEntries(changed.map((r) => [r.key, Number(draft[r.key])])),
                  ...(events === null ? {} : { web_key_events: parsedEvents }),
                })
              }
            >
              Uložit měření webu
            </button>
          </div>
        ) : null}
      </Panel>

      <Panel
        title="Aktivita kontaktu z webu"
        subtitle="Pole aktivity kontaktu (A až D) se zapisuje do SmartEmailingu."
      >
        <div className="row">
          <Toggle
            checked={activityOn}
            disabled={!canWrite || patch.isPending}
            label="Počítat do aktivity kontaktu jen aktivní návštěvy webu"
            onChange={(v) => setAskActivity(v)}
          />
          <span>Počítat do aktivity kontaktu jen aktivní návštěvy webu</span>
        </div>
        <p className="note">
          Vypnuto (výchozí): za aktivitu se počítá i jednosekundové zobrazení stránky. Zapnuto:
          počítají se jen návštěvy s dostatkem aktivního času nebo klíčovou akcí. Změna se projeví
          při nejbližším přepočtu příznaků a hodnoty se pak odešlou do SmartEmailingu.
        </p>
      </Panel>

      <ConfirmDialog
        open={askActivity !== null}
        title={
          askActivity
            ? 'Zapnout aktivitu jen z aktivních návštěv?'
            : 'Vrátit původní výpočet aktivity?'
        }
        confirmLabel={askActivity ? 'Zapnout' : 'Vypnout'}
        busy={patch.isPending}
        onCancel={() => setAskActivity(null)}
        onConfirm={() => patch.mutate({ web_engagement_in_activity: askActivity })}
      >
        <p>
          V prostředí <b>{envName}</b> se u části kontaktů změní hodnota pole aktivity. Nové hodnoty
          se při dalším přepočtu odešlou do SmartEmailingu (pokud je odesílání zapnuté).
        </p>
      </ConfirmDialog>
    </>
  );
}
